/**
 * TypographyModel.js
 * ──────────────────
 * Canonical shape for typography settings used across all CMS blocks.
 * Provides factory, merge, and helper methods so every consumer works
 * with the same structure — and old JSON missing newer keys is handled
 * gracefully via defaults.
 *
 * Usage:
 *   import TypographyModel from '../../models/TypographyModel';
 *
 *   // Create with defaults
 *   var typo = TypographyModel.create();
 *
 *   // Merge saved data (handles missing keys from older versions)
 *   var typo = TypographyModel.create({ fontSize: '1.25rem', fontWeight: '700' });
 *
 *   // Build from prefixed block props  (e.g. titleFontFamily → fontFamily)
 *   var typo = TypographyModel.fromProps(blockProps, 'title');
 *
 *   // Write back to prefixed block props
 *   var changes = TypographyModel.toProps(typo, 'title');
 *   // → { titleFontFamily: '', titleFontSize: '1.25rem', ... }
 *
 *   // Get just the CSS-relevant styles (non-empty values)
 *   var css = TypographyModel.toCssObject(typo);
 *   // → { fontSize: '1.25rem', fontWeight: '700' }
 */

/**
 * The ordered list of keys that make up a typography model.
 * This is the single source of truth — every helper iterates this list.
 */
var TYPOGRAPHY_KEYS = [
	'fontFamily',
	'fontSize',
	'fontWeight',
	'fontStyle',
	'textDecoration',
	'textTransform',
	'lineHeight',
	'letterSpacing',
	'color'
];

/**
 * Create a typography model with defaults, merging in any overrides.
 * Missing or undefined keys always get a safe empty-string default.
 *
 * @param {Object} [overrides]  Partial typography values to merge in
 * @returns {Object}            A complete typography model
 */
function create(overrides) {
	var model = {};
	for (var i = 0; i < TYPOGRAPHY_KEYS.length; i++) {
		var key = TYPOGRAPHY_KEYS[i];
		model[key] = (overrides && overrides[key] !== undefined) ? overrides[key] : '';
	}
	return model;
}

/**
 * Build the flat prop key for a given typography key + prefix.
 * If keyMap has an override for this key, use it; otherwise use the
 * standard capitalised pattern.
 *
 * @param {String} key        Typography key (e.g. 'fontSize')
 * @param {String} prefix     Prefix (e.g. 'title')
 * @param {Object} [keyMap]   Optional overrides { fontSize: 'titleSize' }
 * @returns {String}
 */
function buildPropKey(key, prefix, keyMap) {
	if (keyMap && keyMap[key]) return keyMap[key];
	if (!prefix) return key;
	return prefix + key.charAt(0).toUpperCase() + key.slice(1);
}

/**
 * Build a typography model by extracting prefixed keys from a flat props
 * object.  Handles the common CMS pattern where block props use prefixes
 * like "titleFontFamily", "excerptFontSize", etc.
 *
 * @param {Object} props          Flat props object (e.g. block.props)
 * @param {String} [prefix='']    Key prefix (e.g. 'title', 'excerpt')
 * @param {Object} [defaults={}]  Default values if prop is missing
 * @param {Object} [keyMap=null]  Optional map of { typographyKey: actualPropKey }
 * @returns {Object}              A complete typography model
 */
function fromProps(props, prefix, defaults, keyMap) {
	if (!props) props = {};
	if (!defaults) defaults = {};
	var model = {};
	for (var i = 0; i < TYPOGRAPHY_KEYS.length; i++) {
		var key = TYPOGRAPHY_KEYS[i];
		var propKey = buildPropKey(key, prefix || '', keyMap);
		model[key] = props[propKey] !== undefined ? props[propKey] : (defaults[key] || '');
	}
	return model;
}

/**
 * Convert a typography model back into a flat prefixed-key object
 * suitable for committing to block props.
 *
 * @param {Object} model          Typography model
 * @param {String} [prefix='']    Key prefix
 * @param {Object} [keyMap=null]  Optional map of { typographyKey: actualPropKey }
 * @returns {Object}              Flat object with prefixed keys
 */
function toProps(model, prefix, keyMap) {
	if (!model) model = {};
	var result = {};
	for (var i = 0; i < TYPOGRAPHY_KEYS.length; i++) {
		var key = TYPOGRAPHY_KEYS[i];
		var propKey = buildPropKey(key, prefix || '', keyMap);
		result[propKey] = model[key] !== undefined ? model[key] : '';
	}
	return result;
}

/**
 * Return a new model with a single key updated (immutable pattern).
 *
 * @param {Object} model   Current typography model
 * @param {String} key     The typography key to update (e.g. 'fontSize')
 * @param {String} value   The new value
 * @returns {Object}       New model with the updated key
 */
function set(model, key, value) {
	var updated = create(model);
	updated[key] = value;
	return updated;
}

/**
 * Extract only non-empty values as a CSS-applicable object.
 *
 * @param {Object} model   A typography model
 * @returns {Object}       Object with only non-empty typography CSS properties
 */
function toCssObject(model) {
	if (!model) return {};
	var css = {};
	for (var i = 0; i < TYPOGRAPHY_KEYS.length; i++) {
		var key = TYPOGRAPHY_KEYS[i];
		if (model[key]) {
			css[key] = model[key];
		}
	}
	return css;
}

/**
 * Check whether two typography models are equal.
 *
 * @param {Object} a
 * @param {Object} b
 * @returns {Boolean}
 */
function isEqual(a, b) {
	if (!a && !b) return true;
	if (!a || !b) return false;
	for (var i = 0; i < TYPOGRAPHY_KEYS.length; i++) {
		var key = TYPOGRAPHY_KEYS[i];
		if ((a[key] || '') !== (b[key] || '')) return false;
	}
	return true;
}

/**
 * Get the list of typography property keys.
 * @returns {String[]}
 */
function keys() {
	return TYPOGRAPHY_KEYS.slice();
}

var TypographyModel = {
	KEYS: TYPOGRAPHY_KEYS,
	create: create,
	fromProps: fromProps,
	toProps: toProps,
	buildPropKey: buildPropKey,
	set: set,
	toCssObject: toCssObject,
	isEqual: isEqual,
	keys: keys
};

export default TypographyModel;

